/**
 * Typeahead input for chrome editor destination fields.
 *
 * Replaces plain <input> elements with a combobox that shows:
 *   1. "Site features" -- built-in routes derived from DEFAULT_CANVAS_NAV
 *   2. "Pages"         -- published CMS pages via usePagesForDropdown
 *   3. "Use custom path: ..." fallback when the typed value doesn't match
 *
 * Keyboard: Arrow Up/Down to navigate, Enter to pick, Escape to close.
 * External URLs and anchor links are accepted via the custom-path fallback.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type KeyboardEvent,
} from "react";
import { usePagesForDropdown } from "../custom-url-edit/usePagesForDropdown";
import { DEFAULT_CANVAS_NAV } from "../page-canvas/canvasDefaults";

// ── Types ──────────────────────────────────────────────────

interface DestinationOption {
  label: string;
  path: string;
  group: "feature" | "page";
}

interface DestinationTypeaheadProps {
  /** Current path value (controlled). */
  value: string;
  /** Called on every keystroke and on pick -- same contract as a plain input. */
  onChange: (path: string) => void;
  /** Tailwind classes for the container (width, flex layout, margin). */
  className?: string;
  placeholder?: string;
  /**
   * "inline" (default): borderless right-aligned input for compact row slots,
   * dropdown anchored to the right edge.
   * "field": full-width boxed input matching inspector form fields, dropdown
   * spanning the field width.
   */
  variant?: "inline" | "field";
}

// ── Static feature destinations (derived from canvas nav defaults) ──

const SITE_FEATURES: DestinationOption[] = DEFAULT_CANVAS_NAV.map((n) => ({
  label: n.primary ? "Book" : n.label,
  path: n.key === "default-home" ? "/" : `/${n.key.replace("default-", "")}`,
  group: "feature" as const,
}));

// ── Component ──────────────────────────────────────────────

export function DestinationTypeahead({
  value: rawValue,
  onChange,
  className,
  placeholder = "/path",
  variant = "inline",
}: DestinationTypeaheadProps) {
  // Callers pass config values that may be absent (e.g. an announcement bar
  // with no button URL) — normalize so the input stays controlled.
  const value = rawValue ?? "";
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Pages data ──────────────────────────────────────────────

  const { pages } = usePagesForDropdown();

  const pageOptions: DestinationOption[] = useMemo(
    () =>
      pages.map((p) => ({
        label: !p.slug ? "Home" : (p.title ?? p.slug),
        path: !p.slug ? "/" : `/${p.slug}`,
        group: "page" as const,
      })),
    [pages],
  );

  // ── Filtering ──────────────────────────────────────────────

  const filterText = value.toLowerCase().trim();

  const featureFiltered = useMemo(
    () =>
      filterText
        ? SITE_FEATURES.filter(
            (o) =>
              o.label.toLowerCase().includes(filterText) ||
              o.path.toLowerCase().includes(filterText),
          )
        : SITE_FEATURES,
    [filterText],
  );

  const pageFiltered = useMemo(
    () =>
      filterText
        ? pageOptions.filter(
            (o) =>
              o.label.toLowerCase().includes(filterText) ||
              o.path.toLowerCase().includes(filterText),
          )
        : pageOptions,
    [filterText, pageOptions],
  );

  // Custom-path fallback: show when typed value doesn't exactly match any path
  const trimmed = value.trim();
  const exactMatch =
    featureFiltered.some((o) => o.path === trimmed) ||
    pageFiltered.some((o) => o.path === trimmed);
  const showCustom = open && trimmed.length > 0 && !exactMatch;

  // Flat selectable items for keyboard navigation
  const flatItems: DestinationOption[] = useMemo(() => {
    const items: DestinationOption[] = [
      ...featureFiltered,
      ...pageFiltered,
    ];
    if (showCustom) {
      items.push({
        label: `Use custom path: ${trimmed}`,
        path: trimmed,
        group: "feature",
      });
    }
    return items;
  }, [featureFiltered, pageFiltered, showCustom, trimmed]);

  // Reset active index when filter changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [filterText]);

  // ── Handlers ──────────────────────────────────────────────

  const pick = useCallback(
    (path: string) => {
      onChange(path);
      setOpen(false);
      setActiveIndex(-1);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    if (blurTimer.current !== null) clearTimeout(blurTimer.current);
    setOpen(true);
    setActiveIndex(-1);
  }, []);

  const handleBlur = useCallback(() => {
    blurTimer.current = setTimeout(() => {
      setOpen(false);
      setActiveIndex(-1);
    }, 180);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!open) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < flatItems.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < flatItems.length) {
            pick(flatItems[activeIndex].path);
          } else {
            setOpen(false);
          }
          break;
        case "Escape":
          e.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
          break;
      }
    },
    [open, flatItems, activeIndex, pick],
  );

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-option-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Cleanup
  useEffect(() => () => {
    if (blurTimer.current !== null) clearTimeout(blurTimer.current);
  }, []);

  // ── Render helpers ────────────────────────────────────────

  const featureBase = 0;
  const pageBase = featureFiltered.length;
  const customIndex = featureFiltered.length + pageFiltered.length;

  const hasResults =
    featureFiltered.length > 0 || pageFiltered.length > 0 || showCustom;

  return (
    <div className={`relative ${className ?? ""}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={
          variant === "field"
            ? "w-full border border-ink-6 rounded-[7px] bg-paper px-2.5 py-1.5 font-mono text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all"
            : "w-full bg-transparent border-0 text-right font-mono text-[10px] text-ink-5 px-0 py-0 focus:outline-none focus:ring-0 focus:text-ink"
        }
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />

      {open && hasResults && (
        <div
          ref={listRef}
          role="listbox"
          className={`absolute top-full z-50 mt-1 max-h-[280px] overflow-y-auto rounded-lg border border-ink-5 bg-paper shadow-lg py-1 ${
            variant === "field" ? "left-0 right-0" : "right-0 w-[220px]"
          }`}
        >
          {/* Site features group */}
          {featureFiltered.length > 0 && (
            <>
              <div className="px-3 pt-1.5 pb-1 text-[9.5px] font-bold tracking-[0.07em] uppercase text-ink-5">
                Site features
              </div>
              {featureFiltered.map((opt, i) => {
                const idx = featureBase + i;
                return (
                  <button
                    key={`f-${opt.path}`}
                    type="button"
                    data-option-index={idx}
                    role="option"
                    aria-selected={activeIndex === idx}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt.path)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11.5px] cursor-pointer transition-colors ${
                      activeIndex === idx
                        ? "bg-tide-ss text-tide"
                        : "text-ink-3 hover:bg-paper-2"
                    }`}
                  >
                    <span className="font-medium truncate">{opt.label}</span>
                    <span className="ml-auto font-mono text-[10px] text-ink-5 flex-none">
                      {opt.path}
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {/* Pages group */}
          {pageFiltered.length > 0 && (
            <>
              {featureFiltered.length > 0 && (
                <div className="mx-2 my-1 border-t border-ink-6" />
              )}
              <div className="px-3 pt-1.5 pb-1 text-[9.5px] font-bold tracking-[0.07em] uppercase text-ink-5">
                Pages
              </div>
              {pageFiltered.map((opt, i) => {
                const idx = pageBase + i;
                return (
                  <button
                    key={`p-${opt.path}`}
                    type="button"
                    data-option-index={idx}
                    role="option"
                    aria-selected={activeIndex === idx}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt.path)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11.5px] cursor-pointer transition-colors ${
                      activeIndex === idx
                        ? "bg-tide-ss text-tide"
                        : "text-ink-3 hover:bg-paper-2"
                    }`}
                  >
                    <span className="font-medium truncate">{opt.label}</span>
                    <span className="ml-auto font-mono text-[10px] text-ink-5 flex-none">
                      {opt.path}
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {/* Custom path fallback */}
          {showCustom && (
            <>
              {(featureFiltered.length > 0 || pageFiltered.length > 0) && (
                <div className="mx-2 my-1 border-t border-ink-6" />
              )}
              <button
                type="button"
                data-option-index={customIndex}
                role="option"
                aria-selected={activeIndex === customIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(trimmed)}
                onMouseEnter={() => setActiveIndex(customIndex)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11.5px] cursor-pointer transition-colors ${
                  activeIndex === customIndex
                    ? "bg-tide-ss text-tide"
                    : "text-ink-4 hover:bg-paper-2"
                }`}
              >
                <span className="italic">
                  Use custom path:{" "}
                  <span className="font-mono text-[10px] not-italic">
                    {trimmed}
                  </span>
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
